import {
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
} from "@chakra-ui/react";
import {
  HiOutlineDotsVertical,
  HiOutlinePencil,
  HiOutlineTrash,
} from "react-icons/hi";
import { HiOutlineUserAdd } from "react-icons/hi";
import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";

import {
  useDeleteDealerUserMutation,
  useResetPasswordMutation,
  useSetDealerContactMutation,
} from "src/routes/DealerOrganization/mutations";
import { DealerUser } from "src/common/types";
import { ResetPassword } from "src/common/icons/ResetPassword";
import { DealerOrganizationUsersConfirmation } from "src/routes/DealerOrganization/DealerOrganizationUsersConfirmation";

export type DealerOrganizationUsersActionsMenuProps = {
  dealerUser: DealerUser;
  isPointOfContact?: boolean;
};

export const DealerOrganizationUsersActionsMenu = ({
  dealerUser,
  isPointOfContact,
}: DealerOrganizationUsersActionsMenuProps) => {
  const { id } = useParams<"id">();
  const navigate = useNavigate();

  if (!id) {
    return null;
  }

  const { isOpen, onOpen, onClose } = useDisclosure();

  const mutationOptions = { onSettled: onClose };
  const resetPasswordMutation = useResetPasswordMutation(mutationOptions);
  const deleteDealerUserMutation = useDeleteDealerUserMutation(mutationOptions);
  const setDealerContactMutation = useSetDealerContactMutation(mutationOptions);

  const [{ title, onConfirm }, setConfirmation] = useState({
    title: "Unknown Action",
    onConfirm: () => console.warn("Method onConfirm is not implemented"),
  });

  return (
    <Menu>
      <MenuButton
        as={IconButton}
        aria-label="Actions"
        icon={<HiOutlineDotsVertical />}
        variant="clear"
      >
        Actions
      </MenuButton>
      <MenuList>
        <MenuItem
          isDisabled={isPointOfContact || setDealerContactMutation.isLoading}
          justifyContent="space-between"
          onClick={() => {
            setConfirmation({
              title: "Set as Dealer Contact",
              onConfirm: () =>
                setDealerContactMutation.mutate({
                  dealerOrganizationId: id,
                  dealerUserId: dealerUser.dealerUserId,
                }),
            });

            onOpen();
          }}
        >
          Set as Dealer Contact
          <HiOutlineUserAdd />
        </MenuItem>

        <MenuItem
          justifyContent="space-between"
          onClick={() => {
            navigate(`/dealers/${id}/users/${dealerUser.dealerUserId}/edit`);
          }}
        >
          Edit Staff Member
          <HiOutlinePencil />
        </MenuItem>

        <MenuItem
          isDisabled={isPointOfContact || deleteDealerUserMutation.isLoading}
          justifyContent="space-between"
          onClick={() => {
            setConfirmation({
              title: "Set Dealer Contact",
              onConfirm: () =>
                deleteDealerUserMutation.mutate({
                  dealerOrganizationId: id,
                  dealerUserId: dealerUser.dealerUserId,
                }),
            });

            onOpen();
          }}
        >
          Delete Staff Member
          <HiOutlineTrash />
        </MenuItem>

        <MenuItem
          isDisabled={resetPasswordMutation.isLoading}
          justifyContent="space-between"
          onClick={() => {
            setConfirmation(() => {
              return {
                title: "Reset Password",
                onConfirm: () => {
                  resetPasswordMutation.mutate(dealerUser.dealerUserId);
                },
              };
            });

            onOpen();
          }}
        >
          Reset Password
          <ResetPassword />
        </MenuItem>
      </MenuList>

      <DealerOrganizationUsersConfirmation
        {...{ title, isOpen, onOpen, onClose, onConfirm }}
      />
    </Menu>
  );
};
